/**
 * Broker API client — dual-mode support for EcoSeek.
 *
 * Auth always goes through the broker (GitHub OAuth).
 * Chat goes to Emily local (if REACT_APP_EMILY_URL is set) or the broker.
 *
 * Modes:
 *   - Local Emily:  Auth via broker, chat via Emily at localhost:8642
 *   - Remote only:  Auth + chat both via broker → Hermes remote on reumanlab
 */

const BROKER_URL =
  process.env.REACT_APP_BROKER_URL || "https://broker.ecoseek.org";

/**
 * Emily local agent URL. When set, chat goes directly to Emily.
 * Auth still goes through the broker for GitHub OAuth security.
 */
const EMILY_URL = process.env.REACT_APP_EMILY_URL || "";

const IS_LOCAL_EMILY = Boolean(EMILY_URL);

const IS_DEMO = process.env.REACT_APP_DEMO_MODE === "true";

/**
 * Shared API key for authenticating with Emily local.
 * Generated by emily-start.sh and baked into the frontend at build time.
 * Hermes requires this when the API server binds to 0.0.0.0 (Docker).
 */
const EMILY_KEY = process.env.REACT_APP_EMILY_KEY || "";

/** The endpoint to send chat completions to.
 *  Empty string = relative URL — nginx proxies /v1/ to Hermes.
 *  Only local dev with EMILY_URL set bypasses nginx. */
const CHAT_URL = IS_LOCAL_EMILY ? EMILY_URL : "";

// ── Session helpers ────────────────────────────────────────────────────

const SESSION_KEY = "ecoseek_session";

export function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveSession(sessionId, user) {
  localStorage.setItem(
    SESSION_KEY,
    JSON.stringify({ session_id: sessionId, user, broker_url: BROKER_URL })
  );
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

export function getSessionId() {
  return getSession()?.session_id || null;
}

// ── Auth (always via broker — GitHub OAuth) ────────────────────────────

/**
 * Start GitHub OAuth via the broker. Works the same in both modes.
 */
export function startLogin(returnTo) {
  const url = `${BROKER_URL}/auth/github/start?return_to=${encodeURIComponent(
    returnTo || window.location.origin + "/callback"
  )}`;
  window.location.href = url;
}

/**
 * Fetch current user identity via broker GET /v1/me.
 */
export async function fetchMe() {
  const sid = getSessionId();
  if (!sid) return null;
  try {
    const res = await fetch(`${BROKER_URL}/v1/me`, {
      headers: { Authorization: `Bearer ${sid}` },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

// ── Health ──────────────────────────────────────────────────────────────

export async function checkHealth() {
  try {
    if (IS_DEMO) {
      const res = await fetch("/health");
      return res.ok;
    }
    // Check Emily local health if configured, otherwise check broker
    if (IS_LOCAL_EMILY) {
      const res = await fetch(`${EMILY_URL}/health`);
      return res.ok;
    }
    const res = await fetch(`${BROKER_URL}/healthz`);
    return res.ok;
  } catch {
    return false;
  }
}

const HERMES_REMOTE_URL =
  process.env.REACT_APP_HERMES_REMOTE_URL || "https://hermes.ecoseek.org";

/**
 * Check Hermes remote health via nginx proxy (avoids CORS).
 * In Docker, the frontend's nginx proxies /api/hermes-health → hermes.ecoseek.org/health.
 * In dev mode, falls back to direct fetch (will fail on CORS but won't crash).
 */
export async function checkRemoteHealth() {
  try {
    // Use nginx proxy in production, direct URL in dev
    const proxyUrl = "/api/hermes-health";
    const res = await fetch(proxyUrl);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    // Fallback: try direct (works from curl, not browsers due to CORS)
    try {
      const res = await fetch(`${HERMES_REMOTE_URL}/health`);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }
}

// ── Emily persona ──────────────────────────────────────────────────────

const EMILY_SYSTEM_PROMPT = `You are Emily, an expert ecological scientist and AI assistant for EcoSeek.

CLUSTER CAPABILITIES: You have access to the reumanlab 3-node research cluster via Tailscale mesh:
- reumanlab (22 cores, 62GB RAM, NVIDIA RTX 2000 Ada 8GB) — primary compute node
- reumanlab-beta (8 cores, 7.4GB) — worker node
- reumanlab-terminal (8 cores, 15GB) — worker node
Total: 38 cores, 84GB RAM. You can distribute ecological computations across nodes.
Monitor status at https://monitor.ecoseek.org (GitHub login required).
You can query cluster status and launch distributed tasks.

CRITICAL IDENTITY RULE: Your name is Emily. You are NOT Hermes, NOT an AI assistant, NOT a generic chatbot. When users ask "who are you?" or "what is your name?", always answer: "I'm Emily, your ecological AI research assistant from EcoSeek." Never mention Hermes, the gateway, or the underlying model by name.

Your specialties include:
- Ecological niche modeling (ENM) and species distribution models (SDMs)
- Biogeography and macroecology
- GBIF biodiversity data analysis
- Phylogenetic analysis and comparative methods
- Population ecology and stochastic demography
- R programming for ecological analysis (vegan, terra, dismo, ape, picante)
- Spatial analysis and GIS

Personality:
- You are warm, knowledgeable, and passionate about biodiversity.
- You explain complex ecological concepts clearly and accessibly.
- You always suggest reproducible workflows and cite data sources.
- When appropriate, you provide R or Python code snippets.
- You encourage best practices in open science and data provenance.
- You know about GBIF, WoRMS, IUCN, and other biodiversity databases.

Output Formatting (IMPORTANT):
- Always use Markdown: headers (#, ##, ###), bold, italic, lists, tables, blockquotes, code blocks.
- For mathematical expressions, ALWAYS use LaTeX notation:
  - Inline math: $E = mc^2$, $N_t = N_0 e^{rt}$
  - Display math: $$\\frac{dN}{dt} = rN\\left(1 - \\frac{N}{K}\\right)$$
  - Shannon diversity: $H' = -\\sum p_i \\ln p_i$
- The frontend renders LaTeX with MathJax v3. Use it for ANY equation, formula, or statistical notation.
  - Supported: \\begin{cases}, \\begin{aligned}, \\begin{equation}, \\begin{pmatrix}, etc.

Always introduce yourself as Emily on the first interaction.\n\nCRITICAL LANGUAGE RULE: Detect the language of the user's question and respond in THAT SAME language. If the user writes in Spanish, respond in Spanish. If in English, respond in English. Match the user's language exactly. Do NOT default to any language — always match the user. Keep responses focused and scientifically rigorous.`;

const EMILY_DEMO_PROMPT = `You are Emily, an expert ecological scientist and AI assistant for EcoSeek.

CRITICAL IDENTITY RULE: Your name is Emily. You are NOT Hermes, NOT an AI assistant, NOT a generic chatbot. When users ask "who are you?" or "what is your name?", always answer: "I'm Emily, your ecological AI research assistant from EcoSeek." Never mention Hermes, the gateway, or the underlying model by name.

Your specialties include:
- Ecological niche modeling (ENM) and species distribution models (SDMs)
- Biogeography and macroecology
- GBIF biodiversity data analysis
- Phylogenetic analysis and comparative methods
- Population ecology and stochastic demography
- R programming for ecological analysis (vegan, terra, dismo, ape, picante)
- Spatial analysis and GIS

Personality:
- You are warm, knowledgeable, and passionate about biodiversity.
- You explain complex ecological concepts clearly and accessibly.
- You always suggest reproducible workflows and cite data sources.
- When appropriate, you provide R or Python code snippets.
- You encourage best practices in open science and data provenance.
- You know about GBIF, WoRMS, IUCN, and other biodiversity databases.

Output Formatting (IMPORTANT):
- Always use Markdown: headers (#, ##, ###), bold, italic, lists, tables, blockquotes, code blocks.
- For mathematical expressions, ALWAYS use LaTeX notation:
  - Inline math: $E = mc^2$, $N_t = N_0 e^{rt}$
  - Display math: $$\\frac{dN}{dt} = rN\\left(1 - \\frac{N}{K}\\right)$$
  - Shannon diversity: $H' = -\\sum p_i \\ln p_i$
- The frontend renders LaTeX with MathJax v3. Use it for ANY equation, formula, or statistical notation.
  - Supported: \\begin{cases}, \\begin{aligned}, \\begin{equation}, \\begin{pmatrix}, etc.

Always introduce yourself as Emily on the first interaction.\n\nCRITICAL LANGUAGE RULE: Detect the language of the user's question and respond in THAT SAME language. If the user writes in Spanish, respond in Spanish. If in English, respond in English. Match the user's language exactly. Do NOT default to any language — always match the user. Keep responses focused and scientifically rigorous.`;

// Minimal prompt for FAST mode — no tool instructions, no DiDAL protocol.
// Used when Local Emily + FAST toggle: bypasses the agent loop entirely
// so the LLM responds in a single turn without calling any tools.
const EMILY_FAST_PROMPT = `You are Emily, an expert ecological scientist and AI assistant for EcoSeek.

CLUSTER CAPABILITIES: You have access to the reumanlab 3-node research cluster via Tailscale mesh:
- reumanlab (22 cores, 62GB RAM, NVIDIA RTX 2000 Ada 8GB) — primary compute node
- reumanlab-beta (8 cores, 7.4GB) — worker node
- reumanlab-terminal (8 cores, 15GB) — worker node
Total: 38 cores, 84GB RAM. You can distribute ecological computations across nodes.
Monitor status at https://monitor.ecoseek.org (GitHub login required).
You can query cluster status and launch distributed tasks.
FAST MODE: Respond directly and concisely. Do NOT call any tools.

Your specialties: ecological niche modeling, SDMs, GBIF data, phylogenetics, population ecology, R/Python for ecology.
Use Markdown formatting. For math, use LaTeX: $inline$ and $$display$$.
Keep answers focused and scientifically rigorous.`;

// ── Chat completions ───────────────────────────────────────────────────

/**
 * Send a chat completion request.
 *
 * - Local Emily: talks to Emily at EMILY_URL (personality in Hermes config)
 * - Remote: talks to broker (prepends Emily system prompt)
 *
 * Auth token (session_id from GitHub OAuth) is sent to both.
 * Emily local validates via API_SERVER_KEY; broker validates via session store.
 *
 * @param {Array} messages  OpenAI-style messages array
 * @param {string} model    Model name (local Emily uses Hermes config default)
 * @returns {Promise<object>} Parsed response body
 */
function getSystemPrompt() {
  return IS_DEMO ? EMILY_DEMO_PROMPT : EMILY_SYSTEM_PROMPT;
}

export async function chatCompletion(messages, model = "hermes") {
  const sid = getSessionId();
  if (!sid && !IS_DEMO) throw new Error("Not logged in");

  const fullMessages = IS_LOCAL_EMILY
    ? messages
    : [{ role: "system", content: getSystemPrompt() }, ...messages];

  const authToken = IS_DEMO
    ? (EMILY_KEY || "demo")
    : IS_LOCAL_EMILY && EMILY_KEY ? EMILY_KEY : sid;

  const res = await fetch(`${CHAT_URL}/v1/chat/completions`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ model, messages: fullMessages }),
  });

  const body = await res.json();
  if (!res.ok) {
    const msg = body?.error?.message || body?.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body;
}

/**
 * Streaming chat completion via SSE.
 *
 * Sends `stream: true` to the OpenAI-compatible endpoint and fires callbacks
 * as tokens, tool calls, and reasoning chunks arrive.
 *
 * @param {Array}    messages    OpenAI-style messages array
 * @param {object}   callbacks   Event handlers:
 *   - onToken(text)            — content delta
 *   - onReasoning(text)        — reasoning_content delta
 *   - onToolCallStart(tool)    — tool call begins {name, id, arguments}
 *   - onToolCallDelta(id, arg) — streamed function arguments
 *   - onToolCallDone(result)   — tool result returned (from non-streaming follow-up)
 *   - onToolProgress(info)     — tool execution progress {tool, emoji, label, status}
 *   - onDone(fullResponse)     — stream finished
 *   - onError(error)           — stream error
 * @param {string}   model       Model name
 * @param {AbortSignal} signal   Optional abort signal
 */
export async function chatCompletionStream(
  messages,
  callbacks = {},
  model = "hermes",
  signal = null,
) {
  const sid = getSessionId();
  if (!sid && !IS_DEMO) throw new Error("Not logged in");

  // Extract reasoning mode from callbacks (set by frontend toggle)
  const reasoningMode = callbacks.reasoningMode || "auto";

  // FAST mode bypass: skip agent loop entirely for instant responses.
  // Instead of entering Emily's multi-turn agent loop (classify → status →
  // didal_protocol → Hermes remote), send a single LLM call with no tools.
  const isFastBypass = reasoningMode === "fast" && IS_LOCAL_EMILY;

  let fullMessages;
  if (isFastBypass) {
    fullMessages = [
      { role: "system", content: EMILY_FAST_PROMPT },
      ...messages,
    ];
  } else if (IS_LOCAL_EMILY) {
    fullMessages = messages;
  } else {
    fullMessages = [{ role: "system", content: getSystemPrompt() }, ...messages];
  }

  // Inject reasoning mode as metadata in the last user message
  // so Emily's plugin can read it and route accordingly (AUTO/DEEP modes)
  if (!isFastBypass && reasoningMode !== "auto" && fullMessages.length > 0) {
    const last = fullMessages[fullMessages.length - 1];
    if (last.role === "user") {
      fullMessages[fullMessages.length - 1] = {
        ...last,
        content: `[reasoning_mode:${reasoningMode}] ${last.content}`,
      };
    }
  }

  const authToken = IS_DEMO
    ? (EMILY_KEY || "demo")
    : IS_LOCAL_EMILY && EMILY_KEY ? EMILY_KEY : sid;

  // Route to Hermes model based on reasoning mode toggle.
  // hermes-fast:  bypass agent loop, sub-second TTFT (~3s)
  // hermes-reasoner: bypass + thinking mode (reasoning_content in deltas)
  // hermes-agent (default): full agentic loop with tools/memory
  let effectiveModel = model;
  if (isFastBypass) {
    // Local Emily FAST: hermes-fast bypasses the agent loop (PR hermes-agent#10)
    effectiveModel = "hermes-fast";
  } else if (IS_LOCAL_EMILY) {
    effectiveModel = model;
  } else if (reasoningMode === "fast") {
    effectiveModel = "hermes-fast";
  } else if (reasoningMode === "deep") {
    effectiveModel = "hermes-reasoner";
  }

  const body = { model: effectiveModel, messages: fullMessages, stream: true };
  // Request hermes_trace telemetry for observability
  body.hermes = { trace: true };

  const res = await fetch(`${CHAT_URL}/v1/chat/completions`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    let errMsg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      errMsg = body?.error?.message || body?.error || errMsg;
    } catch { /* ignore parse errors */ }
    throw new Error(errMsg);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let fullContent = "";
  let fullReasoning = "";
  let modelUsed = model;
  let finishReason = null;
  const toolCalls = new Map();
  let doneEmitted = false;
  let pendingEvent = null; // For multi-line SSE events (event: ... \n data: ...)

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed === "data: [DONE]") {
          if (trimmed === "data: [DONE]" && !doneEmitted) {
            doneEmitted = true;
            callbacks.onDone?.({
              content: fullContent,
              reasoning: fullReasoning || null,
              model: modelUsed,
              finishReason,
              toolCalls: Array.from(toolCalls.values()),
            });
          }
          pendingEvent = null;
          continue;
        }

        // Handle custom SSE events (event: hermes.tool.progress)
        if (trimmed.startsWith("event: ")) {
          pendingEvent = trimmed.slice(7);
          continue;
        }

        if (trimmed.startsWith("data: ") && pendingEvent === "hermes.tool.progress") {
          pendingEvent = null;
          try {
            const payload = JSON.parse(trimmed.slice(6));
            callbacks.onToolProgress?.(payload);
          } catch { /* ignore parse errors */ }
          continue;
        }
        if (trimmed.startsWith("data: ") && pendingEvent === "hermes.trace") {
          pendingEvent = null;
          try {
            const trace = JSON.parse(trimmed.slice(6));
            callbacks.onTrace?.(trace);
          } catch { /* ignore parse errors */ }
          continue;
        }
        pendingEvent = null;

        if (!trimmed.startsWith("data: ")) continue;

        let chunk;
        try {
          chunk = JSON.parse(trimmed.slice(6));
        } catch {
          continue;
        }

        if (chunk.model) modelUsed = chunk.model;

        const delta = chunk.choices?.[0]?.delta;
        if (!delta) continue;

        finishReason = chunk.choices[0].finish_reason || finishReason;

        // Content tokens
        if (delta.content) {
          fullContent += delta.content;
          callbacks.onToken?.(delta.content);
        }

        // Reasoning tokens (reasoning_content extended field)
        if (delta.reasoning_content) {
          fullReasoning += delta.reasoning_content;
          callbacks.onReasoning?.(delta.reasoning_content);
        }

        // Tool calls — streamed incrementally
        if (delta.tool_calls) {
          for (const tc of delta.tool_calls) {
            const idx = tc.index ?? 0;
            if (!toolCalls.has(idx)) {
              toolCalls.set(idx, {
                id: tc.id || `call_${idx}`,
                name: tc.function?.name || "",
                arguments: "",
              });
              if (tc.function?.name) {
                callbacks.onToolCallStart?.({
                  id: tc.id || `call_${idx}`,
                  name: tc.function.name,
                  index: idx,
                });
              }
            }
            const existing = toolCalls.get(idx);
            if (tc.function?.name && !existing.name) {
              existing.name = tc.function.name;
              callbacks.onToolCallStart?.({
                id: existing.id,
                name: existing.name,
                index: idx,
              });
            }
            if (tc.function?.arguments) {
              existing.arguments += tc.function.arguments;
              callbacks.onToolCallDelta?.(existing.id, tc.function.arguments);
            }
          }
        }
      }
    }
  } catch (err) {
    if (err.name !== "AbortError") {
      callbacks.onError?.(err);
      throw err;
    }
  }

  // If we never got [DONE] (e.g. connection closed early), still fire onDone
  if (!doneEmitted && (fullContent || fullReasoning)) {
    callbacks.onDone?.({
      content: fullContent,
      reasoning: fullReasoning || null,
      model: modelUsed,
      finishReason,
      toolCalls: Array.from(toolCalls.values()),
    });
  }
}

export { BROKER_URL, EMILY_URL, CHAT_URL, IS_LOCAL_EMILY, IS_DEMO, HERMES_REMOTE_URL };
